import type { ReactNode } from 'react';
import { Checkbox } from '@/shared/ui/checkbox';
import { Input } from '@/shared/ui/input';
import type { AiCapabilities } from '@/api/schemas/ai';
import type {
  ProviderCapabilityChange,
  ProviderFormChange,
  ProviderFormErrors,
  ProviderFormState,
} from './provider-form-types';

/**
 * The four capability flags, in the order an operator reads them.
 *
 * `modelDiscovery` is OPTIONAL and says so here, in the hint: it is a
 * convenience that lets the console list a provider's models, never a
 * requirement. The model identifier is typed by hand either way.
 */
const CAPABILITY_FIELDS: ReadonlyArray<{
  key: keyof AiCapabilities;
  label: string;
  hint: string;
}> = [
  {
    key: 'structuredOutput',
    label: 'Structured output',
    hint: 'The model can be asked to answer against a JSON schema.',
  },
  {
    key: 'jsonMode',
    label: 'JSON mode',
    hint: 'The model can be asked to answer in JSON.',
  },
  {
    key: 'tools',
    label: 'Tools',
    hint: 'The model can call tools the console gives it.',
  },
  {
    key: 'modelDiscovery',
    label: 'Model discovery',
    hint: 'Optional. The provider can list its own models; the model identifier above is typed by hand either way.',
  },
];

export type ProviderConnectionFieldsProps = {
  values: ProviderFormState;
  errors: ProviderFormErrors;
  apiKey: string;
  credentialLabel: string;
  credentialHint: string;
  onChange: ProviderFormChange;
  onApiKeyChange: (value: string) => void;
};

/** Where the provider lives and what it is called. */
export function ProviderConnectionFields(props: ProviderConnectionFieldsProps): ReactNode {
  return (
    <section className="space-y-4 rounded-lg border border-border p-6">
      <h3 className="text-lg font-medium">Connection</h3>

      <div className="grid gap-4 sm:grid-cols-2">
        <Input
          label="Name"
          value={props.values.name}
          onChange={(event) => {
            props.onChange('name', event.currentTarget.value);
          }}
          error={props.errors.name}
          required
          maxLength={120}
          placeholder="Primary provider"
        />
        <Input
          label="Base URL"
          value={props.values.baseUrl}
          onChange={(event) => {
            props.onChange('baseUrl', event.currentTarget.value);
          }}
          error={props.errors.baseUrl}
          hint="Format: https://host/v1"
          required
          maxLength={500}
          placeholder="https://api.example.com/v1"
        />
      </div>

      <Input
        label="Model identifier"
        value={props.values.modelId}
        onChange={(event) => {
          props.onChange('modelId', event.currentTarget.value);
        }}
        error={props.errors.modelId}
        hint="Free text — the exact model name your provider expects. Model discovery is optional and never required."
        required
        maxLength={200}
        placeholder="gpt-4o-mini"
      />

      <Input
        label={props.credentialLabel}
        type="password"
        value={props.apiKey}
        onChange={(event) => {
          props.onApiKeyChange(event.currentTarget.value);
        }}
        hint={props.credentialHint}
        autoComplete="off"
        maxLength={500}
      />
    </section>
  );
}

export type ProviderGenerationFieldsProps = {
  values: ProviderFormState;
  /** The field-level errors, so a value the API would refuse is named HERE. */
  errors: ProviderFormErrors;
  onChange: ProviderFormChange;
};

/** How the provider is asked to generate. */
export function ProviderGenerationFields(props: ProviderGenerationFieldsProps): ReactNode {
  return (
    <section className="space-y-4 rounded-lg border border-border p-6">
      <h3 className="text-lg font-medium">Generation</h3>

      <div className="grid gap-4 sm:grid-cols-3">
        <Input
          label="Timeout (ms)"
          type="number"
          value={props.values.timeoutMs}
          onChange={(event) => {
            props.onChange('timeoutMs', event.currentTarget.value);
          }}
          error={props.errors.timeoutMs}
          min={1}
          step={100}
          placeholder="30000"
        />
        <Input
          label="Max output tokens"
          type="number"
          value={props.values.maxOutputTokens}
          onChange={(event) => {
            props.onChange('maxOutputTokens', event.currentTarget.value);
          }}
          error={props.errors.maxOutputTokens}
          min={1}
          placeholder="Leave empty for the provider default"
        />
        <Input
          label="Temperature"
          type="number"
          value={props.values.temperature}
          onChange={(event) => {
            props.onChange('temperature', event.currentTarget.value);
          }}
          error={props.errors.temperature}
          min={0}
          max={2}
          step={0.1}
          placeholder="Leave empty for the provider default"
        />
      </div>
    </section>
  );
}

export type ProviderCapabilityFieldsProps = {
  values: ProviderFormState;
  onCapabilityChange: ProviderCapabilityChange;
};

/** What the provider can do. Every flag is independent and optional. */
export function ProviderCapabilityFields(props: ProviderCapabilityFieldsProps): ReactNode {
  return (
    <section className="space-y-4 rounded-lg border border-border p-6">
      <h3 className="text-lg font-medium">Capabilities</h3>
      <p className="text-sm text-content-muted">
        What this provider can do. Each one is independent, and none of them is required.
      </p>

      <div className="grid gap-3 sm:grid-cols-2">
        {CAPABILITY_FIELDS.map((field) => (
          <Checkbox
            key={field.key}
            label={field.label}
            hint={field.hint}
            checked={props.values.capabilities[field.key]}
            onChange={(event) => {
              props.onCapabilityChange(field.key, event.currentTarget.checked);
            }}
          />
        ))}
      </div>
    </section>
  );
}
